import { gql, TypedDocumentNode } from '@apollo/client'
import { useMutation } from '@apollo/client/react'
import { Button } from '@repo/shared-client/components/ui'
import { Link, useRouter } from '@tanstack/react-router'
import LogoSymbol from '@web/src/components/primatives/logo-symbol/logo-symbol'
import { useTranslation } from '@web/src/lib/intl/react'
import { XIcon } from 'lucide-react'
import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { env } from 'src/lib/env.client'
import type { UpdateLastVersionMutation, UpdateLastVersionMutationVariables } from 'types/graphql'
import { useAuth } from '../../../hooks/use-auth'

export const UPDATE_LAST_VERSION: TypedDocumentNode<UpdateLastVersionMutation, UpdateLastVersionMutationVariables> =
  gql`
  mutation UpdateLastVersion {
    updateLastVersion {
      id
      lastVersion
    }
  }
`

// Regex patterns for version parsing (defined at top level for performance)
const VERSION_FORMAT_REGEX = /^(.+?)\+(\d+)\.(.+)$/
const VERSION_PATTERN_REGEX = /^v?(\d+)\.(\d+)\.(\d+)$/

const SAYING_KEYS = [
  "Prepare to be amazed (or mildly impressed). We promise it won't crash your computer... probably.",
  "Get ready for some cool new features. No, seriously, they're actually pretty neat.",
  'New stuff inside! And yes, we tested it this time.',
  'Warning: May contain traces of awesome features and bad jokes.',
  'Updates deployed! Our developers are cautiously optimistic.',
  'Fresh features hot off the keyboard! Careful, they might still be warm.',
  'Behold! The fruits of many coffee-fueled coding sessions.',
  'New version alert! Now with 50% more awesome and 100% less bugs (we hope).',
  'Upgrade time! Like a software glow-up, but with actual improvements.',
  'New features incoming! Our developers did their best to make your life easier.',
  'Version update: Because staying the same is boring.',
  "Improvements deployed! Time to explore what we've been cooking.",
  "Update time! We promise it's worth the pixels you're reading this on.",
]

const WhatsNewBanner = () => {
  const { t } = useTranslation()
  const { isAuthenticated, currentUser, refreshSession } = useAuth()
  const [hideBanner, setHideBanner] = useState(false)
  const [navigateToWhatsNew, setNavigateToWhatsNew] = useState(false)
  const [saying, setSaying] = useState<string>('')
  const { navigate } = useRouter()

  const [updateLastVersion] = useMutation(UPDATE_LAST_VERSION, {
    onCompleted: async () => {
      await refreshSession()

      if (navigateToWhatsNew) {
        navigate({ to: '/helpcenter/whatsnew/latest' })
      }
    },
    onError: (error) => {
      toast.error(error.message)
    },
  })

  const showWhatsNew = (lastVersion: string): boolean => {
    try {
      if (!lastVersion) {
        return true
      }

      const currentVersion = env.VITE_APP_VERSION
      if (!currentVersion) {
        return false
      }

      // Parse current version: {tag}+{build}.{hash}
      const currentMatch = currentVersion.match(VERSION_FORMAT_REGEX)
      if (!currentMatch) {
        // Fallback to old format or invalid format
        return false
      }
      const [, currentTag, currentBuild] = currentMatch

      // Parse last version: {tag}+{build}.{hash}
      const lastMatch = lastVersion.match(VERSION_FORMAT_REGEX)
      if (!lastMatch) {
        // If last version doesn't match new format, show banner
        return true
      }
      const [, lastTag, lastBuild] = lastMatch

      // Check if tag matches version pattern (major.minor.bug or vmajor.minor.bug)
      const currentVersionMatch = currentTag.match(VERSION_PATTERN_REGEX)
      const lastVersionMatch = lastTag.match(VERSION_PATTERN_REGEX)

      if (currentVersionMatch && lastVersionMatch) {
        // Both are version numbers, compare major and minor
        const currentMajor = Number.parseInt(currentVersionMatch[1], 10)
        const currentMinor = Number.parseInt(currentVersionMatch[2], 10)
        const lastMajor = Number.parseInt(lastVersionMatch[1], 10)
        const lastMinor = Number.parseInt(lastVersionMatch[2], 10)

        return currentMajor !== lastMajor || currentMinor !== lastMinor
      }
      // At least one is not a version number, compare build numbers
      return currentBuild !== lastBuild
    } catch {
      // Do nothing just fall through
    }

    return false
  }

  const handleClose = () => {
    setHideBanner(true)
    updateLastVersion()
  }

  const handleLinkWhatsNew = () => {
    setHideBanner(true)
    setNavigateToWhatsNew(true)
    updateLastVersion()
  }

  const formatVersionForDisplay = (version: string): string => {
    try {
      const match = version.match(VERSION_FORMAT_REGEX)
      if (match) {
        const [, tag, build] = match
        return `${tag}+${build}`
      }
      // Fallback to original version if format doesn't match
      return version
    } catch {
      return version
    }
  }

  // Set saying only on client side to avoid hydration mismatch
  useEffect(() => {
    const key = SAYING_KEYS[Math.floor(Math.random() * SAYING_KEYS.length)]
    setSaying(t(key))
  }, [t])

  return (
    <>
      {!hideBanner && isAuthenticated && showWhatsNew(currentUser?.profile?.lastVersion) && (
        <div className="border-border border-b bg-card text-card-foreground">
          <div className="relative mx-auto w-full max-w-screen-2xl px-4 py-3 md:px-6 lg:px-8">
            <Button
              aria-label={t('Close banner')}
              className="absolute top-1/2 right-4 z-10 shrink-0 -translate-y-1/2 text-muted-foreground md:right-6 lg:right-8"
              onClick={handleClose}
              size="icon"
              variant="ghost"
            >
              <XIcon />
            </Button>
            <div className="flex flex-row items-center justify-center gap-2 pr-10 md:gap-4 md:pr-12">
              <LogoSymbol imageClassName="w-[26px] shrink-0 animate-[sf-waveplane_2.5s_ease-in-out_infinite]" />
              <div className="flex min-w-0 flex-col gap-0">
                <p className="text-left font-medium text-foreground">
                  {t('Version {{version}} is LIVE! Check out', {
                    version: formatVersionForDisplay(env.VITE_APP_VERSION || ''),
                  })}{' '}
                  <Link
                    className="inline cursor-pointer font-medium text-primary no-underline underline-offset-4 hover:underline focus-visible:rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                    onClick={(event) => {
                      event.preventDefault()
                      handleLinkWhatsNew()
                    }}
                    to="/helpcenter/whatsnew/latest"
                  >
                    {t("What's New!")}
                  </Link>
                </p>
                <p className="text-left font-medium text-muted-foreground md:text-sm">{saying}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default WhatsNewBanner
